<template>
  <div class="apm-backdrop" @click.self="onClose">
    <div class="apm-modal" role="dialog" aria-modal="true">
      <header class="apm-header">
        <div>
          <div class="apm-eyebrow">Attach alternatives</div>
          <div class="apm-title" v-if="primary">
            <span class="apm-title-label">Primary:</span>
            {{ primaryLabel }}
            <span v-if="primary.toothNum" class="apm-tooth">#{{ primary.toothNum }}</span>
          </div>
        </div>
        <button class="apm-close" @click="onClose" aria-label="Close">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <div class="apm-controls">
        <div class="apm-search">
          <i class="fa fa-search apm-search-icon"></i>
          <input
            v-model="searchInput"
            class="apm-search-input"
            placeholder="Search procedures by code or name…"
          />
        </div>
        <div class="apm-priorities" v-if="priorities.length > 0">
          <span class="apm-priority-label">Priority:</span>
          <button
            class="apm-priority-pill"
            :class="{ active: priorityFilter.length === 0 }"
            @click="priorityFilter = []"
          >All</button>
          <button
            v-for="p in priorities"
            :key="p.defNum"
            class="apm-priority-pill"
            :class="{ active: priorityFilter.indexOf(p.defNum) !== -1 }"
            @click="togglePriorityFilter(p.defNum)"
          >{{ p.itemName }}</button>
        </div>
      </div>

      <div class="apm-list tx-scroll">
        <div v-if="filteredCandidates.length === 0" class="apm-empty">
          No procedures match the current filter.
        </div>
        <label
          v-for="proc in filteredCandidates"
          :key="proc.procNum"
          class="apm-row"
          :class="{ 'apm-row-disabled': isDisabled(proc), 'apm-row-checked': selected.indexOf(proc.procNum) !== -1 }"
        >
          <input
            type="checkbox"
            :checked="selected.indexOf(proc.procNum) !== -1"
            :disabled="isDisabled(proc)"
            @change="toggleSelection(proc.procNum)"
          />
          <div class="apm-row-main">
            <div class="apm-row-line">
              <span class="apm-row-label">{{ procLabel(proc) }}</span>
              <span class="apm-row-tooth" v-if="proc.toothNum">#{{ proc.toothNum }}</span>
            </div>
            <div class="apm-row-sub">
              <span class="apm-code">{{ proc.procCode }}</span>
              <span v-if="proc.priorityName" class="apm-pill apm-pill-priority">{{ proc.priorityName }}</span>
              <span v-if="isAlreadyAttached(proc)" class="apm-pill apm-pill-warn">Already attached</span>
              <span v-if="proc.procNum === primary.procNum" class="apm-pill apm-pill-warn">Primary</span>
            </div>
          </div>
          <div class="apm-row-fee bl-tab-num">{{ formatMoney(proc.fee) }}</div>
        </label>
      </div>

      <footer class="apm-footer">
        <div class="apm-summary">
          <strong>{{ selected.length }}</strong>
          {{ selected.length === 1 ? 'procedure' : 'procedures' }} selected
        </div>
        <span class="apm-spacer"></span>
        <button class="apm-btn" @click="onClose">Cancel</button>
        <button
          class="apm-btn-primary"
          :disabled="selected.length === 0"
          @click="onAttach"
        >Attach as alternatives</button>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AltPickerModal',
  props: {
    primary: { type: Object, required: true },
    procedures: { type: Array, default: () => [] },
    priorities: { type: Array, default: () => [] },
    inGroup: { type: Set, default: () => new Set() }, // procNums already in some alt group
  },
  data() {
    return {
      selected: [],
      searchInput: '',
      priorityFilter: [],
    };
  },
  computed: {
    filteredCandidates() {
      const term = (this.searchInput || '').trim().toLowerCase();
      const priorityFilterSet = new Set(this.priorityFilter);
      return this.procedures.filter((p) => {
        if (term) {
          const blob = `${p.procCode || ''} ${p.abbrDesc || ''} ${p.descript || ''}`.toLowerCase();
          if (blob.indexOf(term) === -1) return false;
        }
        if (priorityFilterSet.size > 0 && !priorityFilterSet.has(p.priority)) return false;
        return true;
      });
    },
    primaryLabel() {
      const p = this.primary;
      if (!p) return '';
      return p.abbrDesc || p.descript || p.procCode || `Procedure #${p.procNum}`;
    },
  },
  methods: {
    isDisabled(proc) {
      if (proc.procNum === this.primary.procNum) return true;
      if (this.isAlreadyAttached(proc)) return true;
      return false;
    },
    isAlreadyAttached(proc) {
      return this.inGroup.has ? this.inGroup.has(proc.procNum) : false;
    },
    togglePriorityFilter(defNum) {
      const next = [...this.priorityFilter];
      const idx = next.indexOf(defNum);
      if (idx === -1) next.push(defNum); else next.splice(idx, 1);
      this.priorityFilter = next;
    },
    toggleSelection(procNum) {
      const idx = this.selected.indexOf(procNum);
      if (idx === -1) this.selected = [...this.selected, procNum];
      else this.selected = this.selected.filter((x) => x !== procNum);
    },
    onClose() { this.$emit('close'); },
    onAttach() {
      if (this.selected.length === 0) return;
      this.$emit('attach', { primaryProcNum: this.primary.procNum, altProcNums: [...this.selected] });
    },
    procLabel(p) { return p.abbrDesc || p.descript || p.procCode || '—'; },
    formatMoney(n) {
      if (!n) return '—';
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.apm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.apm-modal {
  background: #fff;
  width: 100%;
  max-width: 640px;
  max-height: calc(100vh - 56px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.apm-modal * { box-sizing: border-box; }

.apm-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e4e7ec;
}
.apm-eyebrow {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #94a3b8;
  font-weight: 600;
}
.apm-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.apm-title-label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.apm-tooth {
  display: inline-block;
  padding: 1px 6px;
  background: #e6f1ef;
  color: #0d5f54;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.apm-close {
  margin-left: auto;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #475569;
  cursor: pointer;
}
.apm-close:hover { background: #f1f3f6; }

.apm-controls {
  padding: 12px 20px;
  border-bottom: 1px solid #e4e7ec;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.apm-search { position: relative; }
.apm-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 12px;
}
.apm-search-input {
  width: 100%;
  padding: 7px 10px 7px 30px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  font-size: 13px;
  background: #f7f8fa;
}
.apm-search-input:focus {
  outline: none;
  border-color: #0d5f54;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}

.apm-priorities {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
}
.apm-priority-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 500;
  margin-right: 4px;
}
.apm-priority-pill {
  padding: 3px 9px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #475569;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
}
.apm-priority-pill:hover { background: #f1f3f6; }
.apm-priority-pill.active {
  background: #e6f1ef;
  border-color: #0d5f54;
  color: #094840;
}

.apm-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 8px;
}
.apm-empty {
  padding: 28px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

.apm-row {
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  margin: 3px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
}
.apm-row:hover { background: #f7f8fa; }
.apm-row-checked { background: #ecf5f3; }
.apm-row-disabled { opacity: 0.55; cursor: not-allowed; }
.apm-row-disabled:hover { background: transparent; }

.apm-row input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #0d5f54;
}
.apm-row-main { min-width: 0; }
.apm-row-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
}
.apm-row-label {
  font-size: 13px;
  font-weight: 550;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.apm-row-tooth {
  font-size: 11px;
  color: #0d5f54;
  background: #e6f1ef;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.apm-row-sub {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #94a3b8;
}
.apm-code {
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
.apm-pill {
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2px;
  background: #f1f3f6;
  color: #475569;
}
.apm-pill-priority { background: #e6f1ef; color: #0d5f54; }
.apm-pill-warn { background: #fef3c7; color: #b45309; }

.apm-row-fee {
  font-size: 12px;
  color: #475569;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.apm-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #e4e7ec;
  background: #fafafa;
}
.apm-summary {
  font-size: 12px;
  color: #475569;
}
.apm-summary strong { color: #0f172a; }
.apm-spacer { flex: 1; }
.apm-btn,
.apm-btn-primary {
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.apm-btn:hover { background: #f1f3f6; }
.apm-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: white;
}
.apm-btn-primary:hover:not(:disabled) { background: #094840; }
.apm-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
</style>
